import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Heart, User, MessageSquare } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function AdminOracoes() {
  const [filter, setFilter] = useState('all');
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [notes, setNotes] = useState('');
  const queryClient = useQueryClient();

  const { data: prayers = [], isLoading } = useQuery({
    queryKey: ['adminPrayers'],
    queryFn: () => base44.entities.PrayerRequest.list('-created_date', 100)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PrayerRequest.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminPrayers'] });
      toast.success('Pedido atualizado!');
      setSelectedPrayer(null);
    }
  });

  const filteredPrayers = filter === 'all' 
    ? prayers 
    : prayers.filter(p => p.status === filter);

  const statusColors = {
    'Novo': 'bg-blue-100 text-blue-700',
    'Em oração': 'bg-amber-100 text-amber-700',
    'Finalizado': 'bg-green-100 text-green-700'
  };

  const handleStatusChange = (prayer, newStatus) => {
    updateMutation.mutate({ id: prayer.id, data: { status: newStatus } });
  };

  const handleSaveNotes = () => {
    if (selectedPrayer) {
      updateMutation.mutate({ 
        id: selectedPrayer.id, 
        data: { admin_notes: notes } 
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  const newCount = prayers.filter(p => p.status === 'Novo').length;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Pedidos de Oração" 
            subtitle={`${newCount} novos pedidos`}
            showBack={true}
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Filter */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {['all', 'Novo', 'Em oração', 'Finalizado'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filter === status
                  ? 'bg-rose-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {status === 'all' ? 'Todos' : status}
              {status === 'Novo' && newCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                  {newCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {filteredPrayers.length === 0 ? (
          <EmptyState
            icon={Heart}
            title="Nenhum pedido encontrado"
            description="Não há pedidos de oração."
          />
        ) : (
          <div className="space-y-4">
            {filteredPrayers.map((prayer, index) => (
              <motion.div
                key={prayer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-xl shadow-sm p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                      {prayer.is_anonymous ? (
                        <User className="w-4 h-4 text-rose-600" />
                      ) : (
                        <span className="text-sm font-medium text-rose-600">
                          {prayer.name?.[0]?.toUpperCase() || 'A'}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {prayer.is_anonymous ? 'Anônimo' : (prayer.name || 'Sem nome')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(prayer.created_date), "d 'de' MMM 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                  <Badge className={statusColors[prayer.status]}>{prayer.status}</Badge>
                </div>

                <p className="text-gray-700 mb-4">{prayer.request}</p>

                <div className="flex items-center justify-between pt-3 border-t">
                  <Select 
                    value={prayer.status} 
                    onValueChange={(v) => handleStatusChange(prayer, v)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Novo">Novo</SelectItem>
                      <SelectItem value="Em oração">Em oração</SelectItem>
                      <SelectItem value="Finalizado">Finalizado</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedPrayer(prayer);
                      setNotes(prayer.admin_notes || '');
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Notas
                  </Button>
                </div>

                {prayer.admin_notes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    <strong className="text-gray-700">Notas:</strong> {prayer.admin_notes}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Notes Modal */}
      {selectedPrayer && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPrayer(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-md p-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Notas do Administrador</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione notas sobre este pedido..."
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setSelectedPrayer(null)}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSaveNotes}
                disabled={updateMutation.isPending}
              >
                Salvar
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}